/**
 * Helpers for unread message badges and conversation indicators.
 */
import { isAiConversationType } from '../constants/conversation';
import type { Conversation } from '../types';

export const shouldCountConversationUnread = (
  conversation: Conversation,
): boolean => {
  if (conversation.isBlocked) return false;
  if (conversation.category === 'ai') return false;
  if (isAiConversationType(conversation.conversationType)) return false;
  return true;
};

export const resolveConversationUnreadCount = (
  conversation: Conversation,
): number => {
  if (!shouldCountConversationUnread(conversation)) return 0;
  if (
    typeof conversation.unreadCount === 'number' &&
    Number.isFinite(conversation.unreadCount)
  ) {
    return Math.max(0, conversation.unreadCount);
  }
  return conversation.isUnread ? 1 : 0;
};

export const conversationHasUnread = (conversation: Conversation): boolean =>
  resolveConversationUnreadCount(conversation) > 0;

/**
 * Narrows a message id to one the Conversation API can address. Optimistic
 * local ids and the `"undefined"` a missing field produces are rejected.
 */
export const toBackendMessageId = (
  value: string | number | null | undefined,
): number | null => {
  if (value == null) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

/**
 * Identity of the latest list activity, used to skip stale unread hydrations
 * and to decide whether a thread still owes a read receipt.
 *
 * `MarkMessageRead` clears a whole conversation, so no message id is needed to
 * acknowledge one. That matters here because `Conversation/List` does not
 * return `lastMessageId` at all: the timestamp is the only half of this key
 * that moves, and it is what makes a newly arrived message ask for a fresh
 * receipt on a thread that was already read.
 */
export const conversationUnreadFingerprint = (
  conversation: Conversation,
): string =>
  `${conversation.lastMessageId ?? ''}|${conversation.lastMessageAt ?? 0}`;

/**
 * Keep unread totals across list refreshes/search/pagination.
 * Incoming rows update their own ids; other conversations are left intact.
 */
export const mergeUnreadByConversationId = (
  previous: Record<string, number>,
  conversations: Conversation[],
  options?: { removeIds?: string[] },
): Record<string, number> => {
  const next = { ...previous };
  for (const id of options?.removeIds ?? []) {
    delete next[id];
  }
  for (const conversation of conversations) {
    if (!shouldCountConversationUnread(conversation)) {
      delete next[conversation.id];
      continue;
    }
    next[conversation.id] = resolveConversationUnreadCount(conversation);
  }
  return next;
};

export const sumUnreadByConversationId = (
  unreadById: Record<string, number>,
): number =>
  Object.values(unreadById).reduce(
    (total, count) => total + Math.max(0, count),
    0,
  );

/**
 * Threads carrying unread messages, not messages: a conversation with five
 * unread replies counts once, so two such conversations answer 2.
 */
export const countUnreadConversations = (
  unreadById: Record<string, number>,
): number =>
  Object.values(unreadById).reduce(
    (total, count) => (count > 0 ? total + 1 : total),
    0,
  );

/** Total unread messages across conversation rows already in MessageContext. */
export const sumUnreadMessageCount = (conversations: Conversation[]): number =>
  conversations.reduce(
    (total, conversation) =>
      total + resolveConversationUnreadCount(conversation),
    0,
  );

/**
 * Whether both rows still describe the same latest message.
 *
 * Ids decide it only when both sides actually carry one. `Conversation/List`
 * never returns `lastMessageId`, so comparing it there answered "same" for
 * every row on every refresh — and "different" for a row the send handler had
 * stamped with an id locally. The timestamp is the field the list does move.
 */
const isSameLastMessage = (
  previous: Conversation,
  incoming: Conversation,
): boolean => {
  const previousId = previous.lastMessageId ?? null;
  const incomingId = incoming.lastMessageId ?? null;
  if (previousId != null && incomingId != null) {
    return String(previousId) === String(incomingId);
  }
  return (previous.lastMessageAt ?? 0) === (incoming.lastMessageAt ?? 0);
};

/**
 * Reconcile a refreshed row's unread count with what we already showed.
 *
 * A stated count wins, including `0`. Only the server knows which messages
 * carry a read receipt, and treating `0` as "no answer" meant a count the
 * client had inflated could never come back down — the sender of a message
 * kept seeing it as unread to themselves. Inference is for payloads that
 * genuinely omit the field.
 */
export const applyUnreadToConversation = (
  incoming: Conversation,
  previous?: Conversation,
): Conversation => {
  const incomingHasCount = typeof incoming.unreadCount === 'number';
  const incomingCount = incomingHasCount
    ? Math.max(0, incoming.unreadCount as number)
    : undefined;
  const previousCount = previous
    ? resolveConversationUnreadCount(previous)
    : 0;
  const sameLastMessage =
    previous != null && isSameLastMessage(previous, incoming);

  let unreadCount: number;
  if (incomingCount != null) {
    unreadCount = incomingCount;
  } else if (previous) {
    unreadCount = previousCount;
  } else {
    unreadCount = 0;
  }

  if (
    previous?.lastMessageByMe === true &&
    previousCount === 0 &&
    incomingCount != null &&
    incomingCount > 0
  ) {
    // The backend counts a message as unread for anyone without a read
    // receipt, including the author. The send handler stamps the row with
    // lastMessageByMe: true, unreadCount: 0, and fires markConversationRead —
    // but a list refresh that reaches the server before the receipt POST
    // lands sees the author's own message counted as 1. Suppress it: the
    // viewer sent this message and already has 0 unread. The 30-second window
    // covers client-server clock skew; a genuine peer reply carries a
    // lastMessageAt outside it and is trusted. After this merge the incoming
    // row's lastMessageByMe (false for list payloads) takes over, so the guard
    // fires only once — enough for the receipt to have cleared the server.
    const delta = Math.abs(
      (incoming.lastMessageAt ?? 0) - (previous.lastMessageAt ?? 0),
    );
    if (delta < 30_000) {
      unreadCount = 0;
    }
  }

  if (previous && previousCount === 0 && sameLastMessage) {
    // Read here already, with no newer message since: a count still in flight
    // from before the receipt landed must not put the badge back.
    unreadCount = 0;
  } else if (previous && !sameLastMessage && incomingCount == null) {
    // The row's latest message changed with no count to go by, so assume one
    // unread — but not for the viewer's own message, and not when the payload
    // simply omitted the id it was compared on (no newer activity than we hold).
    const hasNewerActivity =
      (incoming.lastMessageAt ?? 0) > (previous.lastMessageAt ?? 0);
    unreadCount =
      hasNewerActivity && incoming.lastMessageByMe !== true
        ? Math.max(1, previousCount)
        : previousCount;
  }

  if (!shouldCountConversationUnread(incoming)) {
    unreadCount = 0;
  }

  return {
    ...incoming,
    unreadCount,
    isUnread: unreadCount > 0,
  };
};

/** Badge label; returns null when there is nothing to show. */
export const formatUnreadBadgeCount = (count: number): string | null => {
  if (count <= 0) return null;
  return count > 99 ? '99+' : String(count);
};

/**
 * Badge label for a count derived from a single list page. Reaching the page
 * size means the answer is a floor, not a total, so the label says so (`20+`)
 * rather than claiming a precision the payload cannot back.
 */
export const formatPagedUnreadBadgeCount = (
  count: number,
  pageSize: number,
): string | null => {
  if (count <= 0) return null;
  return count >= pageSize ? `${pageSize}+` : String(count);
};
